/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useQuery, useQueryClient } from 'react-query';
import type { FunctionComponent } from 'react';
import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { model, repository } from '@whizz/api-client';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { CancelTopicFocusType, SetTopicFocusType } from '../../forms/types';
import type { SetTopicModalProps } from './modules/modals/SetTopicModal';
import type { CancelTopicModalProps } from './modules/modals/CancelTopicModal';
import type { ToggleModalProps } from '../../components/modal/types';
import { GlobalDashboardLayout } from '../../layouts';
import { CancelTopicModal, SetTopicModal, Settings, TopicFocusTable } from './modules';
import { Alert, Button } from '../../components';
import { UpdateViewButton } from '../../global';
import { useModal } from '../../hooks/useModal';
import { useClassSelector } from '../../hooks/filters/useClassSelector';
import { GLOBAL_ICONS, PROGRESSIONS_MAX_NUM, QUERIES_IDS } from '../../util/vars';
import NoClasses from '../../global/sections/NoClasses';

const TopicFocus: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const teacherId = (me as Teacher).getId();

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
    },
  );

  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'space-between');
  const [ showNoClasses, setShowNoClasses ] = useState(false);

  const topicFocusReportQuery = useQuery(
    [ QUERIES_IDS.TOPIC_FOCUS_REPORT, classValue?.value ],
    () => repository.GroupTopicFocusRepository.byGroupId(classValue?.value, locale),
    {
      enabled: null != classValue?.value,
      placeholderData: [],
      staleTime: 120,
      onSuccess: (topicReplayEntries) => {
        setPropsForSetTopicModal((prev) => ({
          ...prev,
          topicFocusEntries: topicReplayEntries,
        }));
        setPropsForCancelTopicModal((prev) => ({
          ...prev,
          topicFocusEntries: topicReplayEntries,
        }));
      },
    },
  );

  useQuery(
    [ QUERIES_IDS.TOPIC_STUDENTS, classValue?.value ],
    () => model.TopicStudentsEntry.byGroupId(classValue?.value, locale),
    {
      enabled: null != classValue?.value,
      placeholderData: [],
      onSuccess: (topicStudentsEntries) => {
        setPropsForSetTopicModal((prev) => ({
          ...prev,
          topicStudents: topicStudentsEntries,
        }));
      },
    },
  );

  const [ setTopicModal, toggleSetTopicModal, setPropsForSetTopicModal ] = useModal(
    (props: SetTopicModalProps & Pick<ToggleModalProps, 'toggle'>) => (
      <SetTopicModal {...props} />
    ), {
      size: 'lg',
      title: translate('pages.TOPIC.SET.MODAL.title'),
      topicFocusEntries: [],
      topicStudents: [],
      onSubmit: (values: SetTopicFocusType) => {
        const start = DateTime.fromJSDate(values.startDate!).toISO();
        //we include the start date in the interval, so if the interval should last 1 day, we add 0
        const end = DateTime.fromJSDate(values.startDate!).plus({ days: values.endDate - 1 }).toISO();
        return model.StudentTopicFocus.createMultiple(
          values.set_students.map((studentId: string) => ({
            studentId: parseInt(studentId),
            topicId: values.topic!.value,
            durationProgression: PROGRESSIONS_MAX_NUM,
            durationStartDate: start,
            durationEndDate: end,
          })),
        )
          .then(() => {
            queryClient.invalidateQueries([ QUERIES_IDS.TOPIC_FOCUS_REPORT ]);
            queryClient.invalidateQueries([ QUERIES_IDS.TOPIC_STUDENTS ]);
          });
      },
    },
  );

  const [ cancelTopicModal, toggleCancelTopicModal, setPropsForCancelTopicModal ] = useModal(
    (props: CancelTopicModalProps & Pick<ToggleModalProps, 'toggle'>) => (
      <CancelTopicModal {...props} />
    ), {
      size: 'lg',
      title: translate('pages.TOPIC.CANCEL.MODAL.title'),
      topicFocusEntries: [],
      onSubmit: (values: CancelTopicFocusType) => {
        return model.StudentTopicFocus.removeMultiple(
          values.cancel_students.map((studentId) => ({
            studentId: parseInt(studentId),
          })),
        )
          .then(() => {
            queryClient.invalidateQueries([ QUERIES_IDS.TOPIC_FOCUS_REPORT ]);
            queryClient.invalidateQueries([ QUERIES_IDS.TOPIC_STUDENTS ]);
          });
      },
    },
  );

  useEffect(() => {
    if (classesQuery.isFetched && 0 === classesQuery.data?.length) {
      setShowNoClasses(true);
    }
  }, [ classesQuery ]);

  const content = () => {
    if (null == classValue) {
      return (
        <Alert show>{translate('pages.TOPIC.SELECT_CLASS')}</Alert>
      );
    }

    if (!topicFocusReportQuery.isFetched || topicFocusReportQuery.isFetching) {
      // The loader will overwrite it
      return null;
    }

    if (topicFocusReportQuery.isSuccess && 0 === topicFocusReportQuery.data.length) {
      return (
        <Alert show>{translate('pages.TOPIC.NO_STUDENTS')}</Alert>
      );
    }

    return (
      <TopicFocusTable data={topicFocusReportQuery.data!} />
    );
  };

  return (
    <GlobalDashboardLayout
      isLoading={null != classValue && (!topicFocusReportQuery.isFetched || topicFocusReportQuery.isFetching)}
      title={translate('pages.TOPIC.HEADER')}
      actionContent={classSelector}
      settings={(
        <Settings
          updateView={(
            <UpdateViewButton
              onClick={() => {
                if (null == classValue?.value) {
                  return;
                }

                topicFocusReportQuery.refetch();
              }}
            />
          )}
          setTopicButton={(
            <Button
              disabled={(topicFocusReportQuery.data ?? []).length === 0}
              color={'white'}
              icon={GLOBAL_ICONS.topic_focus}
              onClick={() => {
                toggleSetTopicModal(true);
              }}
            >
              {translate('pages.TOPIC.SET.MODAL.title')}
            </Button>
          )}
          cancelTopicButton={(
            <Button
              disabled={!topicFocusReportQuery.data?.some((student) => student.topicFocus.current !== null)}
              color={'white'}
              icon={GLOBAL_ICONS.delete}
              onClick={() => {
                toggleCancelTopicModal(true);
              }}
            >
              {translate('pages.TOPIC.CANCEL.MODAL.title')}
            </Button>
          )}
        />
      )}
    >
      {setTopicModal}
      {cancelTopicModal}

      {
        showNoClasses ? <NoClasses /> : content()}
    </GlobalDashboardLayout>
  );
};

export default TopicFocus;
